import { useTranslate } from '@ur-solutions/react-hooks'
import { ExportButton } from '@/components_deprecated'
import { Cell, Table as BaseTable } from '@/components_deprecated/Table'
import { format, parseISO } from 'date-fns'
import { isModuleName, ModuleName } from '@/modules/companies'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { API_URL } from '@/utils/env'
import { downloadFile } from '@/utils/file'
import type { UserExportNode } from '../types.graphql'
import type { ExportStatus } from '../types'
import { UserExportProgress } from './UserExportProgress'

const UserExportsTableWrapper = styled.div``

const Table = styled((props: React.ComponentProps<typeof BaseTable>) => (
  <BaseTable {...props} />
))`
  position: relative;
  overflow: unset;

  ${props => props.theme.media.largePad},
  ${props => props.theme.media.mobile} {
    overflow: auto;
  }

  td.absence-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 12px 1rem;
    height: 56px;

    span {
      color: ${props => props.theme.colors.gray5};
      font-weight: 600;
    }
  }
`

interface TableRowProps {
  renderBold: boolean
}
const TableRow = styled.tr<TableRowProps>`
  td {
    font-weight: ${props => (props.renderBold ? 'bold' : '500')} !important;

    span.no-name {
      font-style: italic;
      color: ${props => props.theme.colors.gray5};
    }
  }
`

interface UserExportsTableProps {
  userExports: UserExportNode[]
  loading: boolean
  isFirstLoad: boolean
  lastVisit: Date | undefined
}

export const UserExportsTable: React.FC<UserExportsTableProps> = ({
  userExports,
  loading,
  isFirstLoad,
  lastVisit,
}) => {
  const translations = useTranslate({
    noResults: 'common.no-results',
    name: 'common.name',
    time: 'common.time',
    download: 'common.download',
    module: 'common.module',
    progress: 'exports.progress',
    salary: 'common.salary',
    users: 'common.users',
    pending: 'exports.status.pending',
    error: 'exports.status.error',

    activities: 'common.activities',
    absences: 'common.absences',
    forum: 'common.forum',
    handbook: 'common.handbook',
    infringements: 'common.infringements',
    issues: 'common.issues',
    terminal: 'common.terminal',
    timesheets: 'common.timesheets',
    vehicles: 'common.vehicle',
    routePlanner: 'common.route-planner',
    formCreator: 'common.formcreator',
  })

  const [xhrLoading, setXhrLoading] = useState(false)

  const getModuleNames = useCallback(
    (name: string | null) => {
      if (!isModuleName(name ?? '')) return '?'

      switch (name) {
        case ModuleName.ACTIVITIES:
          return translations.activities
        case ModuleName.ABSENCES:
          return translations.absences
        case ModuleName.FORUM:
          return translations.forum
        case ModuleName.HANDBOOK:
          return translations.handbook
        case ModuleName.INFRINGEMENTS:
          return translations.infringements
        case ModuleName.ISSUES:
          return translations.issues
        case ModuleName.TERMINAL:
          return translations.terminal
        case ModuleName.TIMESHEETS:
          return translations.timesheets
        case ModuleName.VEHICLES:
          return translations.vehicles
        case ModuleName.ROUTEPLANNER:
          return translations.routePlanner
        case ModuleName.SALARY:
          return translations.salary
        case ModuleName.FORMCREATOR:
          return translations.formCreator
        case ModuleName.USERS:
          return translations.users
        default:
          return '?'
      }
    },
    [
      translations.absences,
      translations.activities,
      translations.formCreator,
      translations.forum,
      translations.handbook,
      translations.infringements,
      translations.issues,
      translations.routePlanner,
      translations.salary,
      translations.terminal,
      translations.timesheets,
      translations.users,
      translations.vehicles,
    ]
  )

  const getExportStatus = useCallback(
    (userExport: UserExportNode): ExportStatus => {
      if (userExport.exception !== null && userExport.exception !== '')
        return {
          status: 'error',
          message: userExport.exception,
        }

      if (userExport.progress === -1)
        return {
          status: 'error',
          message: 'Unknown error',
        }

      if (userExport.progress < 100)
        return {
          status: 'pending',
          message: userExport.progress,
        }

      if (userExport.progress === 100 && !userExport.file)
        return {
          status: 'error',
          message: 'File not found',
        }

      if (userExport.progress === 100 && !!userExport.file)
        return {
          status: 'success',
          message: `${API_URL}/download-user-export/${userExport.id}`,
        }

      return {
        status: 'error',
        message: 'Unknown error',
      }
    },
    []
  )

  const handleDownload = useCallback((link: string) => {
    return async () => {
      try {
        setXhrLoading(true)
        await downloadFile(link)
      } finally {
        setXhrLoading(false)
      }
    }
  }, [])

  return (
    <UserExportsTableWrapper>
      <Table
        loading={loading && isFirstLoad}
        noData={userExports.length === 0}
        noDataText={translations.noResults}
      >
        <thead>
          <tr>
            <th>{translations.name}</th>
            <th>{translations.time}</th>
            <th>{translations.module}</th>
            <th>{translations.progress}</th>
            <th>{translations.download}</th>
          </tr>
        </thead>

        <tbody>
          {userExports.map(userExport => {
            const date = parseISO(userExport.createdAt)
            const time = format(date, 'dd.MM.yyyy HH:mm')
            const createdAt = new Date(userExport.createdAt)
            const renderBold = lastVisit ? createdAt > lastVisit : false
            const moduleName = getModuleNames(userExport.module)
            const exportStatus = getExportStatus(userExport)

            return (
              <TableRow key={userExport.id} renderBold={renderBold}>
                <Cell>
                  {userExport.name || (
                    <span className="no-name">
                      {exportStatus.status === 'pending'
                        ? translations.pending
                        : exportStatus.status === 'error'
                        ? translations.error
                        : '?'}
                    </span>
                  )}
                </Cell>

                <Cell>{time}</Cell>

                <Cell>{moduleName}</Cell>

                <Cell narrow>
                  <UserExportProgress status={exportStatus} />
                </Cell>

                <Cell narrow>
                  {exportStatus.status === 'success' && (
                    <div>
                      <ExportButton
                        iconLeftProps={{ icon: 'download' }}
                        width="160px"
                        fullWidth={false}
                        onClick={handleDownload(exportStatus.message)}
                        disabled={xhrLoading}
                      >
                        {/* TODO not correct icon */}
                        <span>{translations.download}</span>
                      </ExportButton>
                    </div>
                  )}
                </Cell>
              </TableRow>
            )
          })}
        </tbody>
      </Table>
    </UserExportsTableWrapper>
  )
}
